// Fleet telemetry for ftwcode.ai.
//
// The unit of analysis here is a drone, not a visitor. Every Hopper carries
// its own serial, readable over the BLE Device Information Service, and that
// serial is what FTW's own sales records are keyed to. So the school, the
// district and the buyer are joined in on the dashboard against records FTW
// already holds, and never travel with the event.
//
// WHAT IS DELIBERATELY NOT SENT
//
//   No IP and no geolocation. Location comes from the serial-to-buyer join,
//   which is both more accurate and does not touch the person at the keyboard.
//
//   No identity of any kind. ftwcode.ai has no accounts and should not grow
//   one through the back door of analytics.
//
//   No program content. A saved program stored against a serial at a given
//   minute is reconstructible to a specific student, because the school knows
//   who had that drone in third period. Shape is kept (how many blocks, which
//   types, how deeply nested) and the program itself is not.
//
// What that leaves is everything a manufacturer legitimately wants: which
// units are flying, when, on what firmware, how hard they are being used and
// where they are failing.

// Which build this is. ftwcode.ai is Hopper only. The Mambo platform is the
// same codebase from before the retired blocks were removed, deployed
// separately, and it reports here too. Without this field the two sites'
// numbers are indistinguishable.
const PLATFORM = 'ftwcode';

const ENDPOINT = 'https://ftw-robotics.ai/.netlify/functions/ftwcode-telemetry';

let session = null;

function post (body) {
  try {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.assign({ platform: PLATFORM }, body)),
      keepalive: true
    }).catch(() => {});
  } catch (e) { /* offline, blocked, or no fetch. Never surfaces. */ }
}

// Called once a drone is paired and its serial has been read.
export function startSession (serial, firmware, droneType, diag) {
  session = {
    serial: serial || '',
    firmware: firmware || 'unknown',
    drone: droneType || 'unknown',
    startedAt: Date.now(),
    runs: 0,
    clean: 0,
    errors: 0
  };
  post({
    kind: 'connect',
    serial: session.serial,
    firmware: session.firmware,
    drone: session.drone,
    // Why a serial is or is not present. Without this a missing serial is
    // indistinguishable from nobody having connected.
    serial_source: (diag && diag.serialSource) || 'none',
    info_read: (diag && diag.infoRead) || 'not-attempted'
  });
}

// Called at the end of every program run, whichever view it was run from.
// `types` is a census of block type to count, not the program.
export function recordRun (info) {
  if (!session) return;
  session.runs++;
  if (info.outcome === 'clean') session.clean++;
  if (info.outcome === 'error') session.errors++;
  post({
    kind: 'run',
    serial: session.serial,
    firmware: session.firmware,
    drone: session.drone,
    view: info.view || 'blocks',
    blocks: info.blocks || 0,
    depth: info.depth || 0,
    types: info.types || {},
    outcome: info.outcome || 'unknown',
    error: info.error || ''
  });
}

// Called on disconnect, and on the way out of the page, so a session that
// ends by closing the tab is still counted.
export function endSession () {
  if (!session) return;
  post({
    kind: 'session',
    serial: session.serial,
    firmware: session.firmware,
    drone: session.drone,
    seconds: Math.round((Date.now() - session.startedAt) / 1000),
    runs: session.runs,
    clean: session.clean,
    errors: session.errors
  });
  session = null;
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', endSession);
}
